import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const TestingTools = () => {
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});

  const executeAction = async (action, endpoint, method = 'POST') => {
    setLoading(prev => ({ ...prev, [action]: true }));
    setResults(prev => ({ ...prev, [action]: null }));

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = method === 'DELETE'
        ? await axios.delete(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}${endpoint}`, config)
        : await axios.post(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}${endpoint}`, {}, config);

      setResults(prev => ({
        ...prev,
        [action]: {
          success: true,
          message: response.data.message
        }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [action]: {
          success: false,
          message: error.response?.data?.message || error.message
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [action]: false }));
    }
  };

  const tools = [
    {
      id: 'clean',
      title: 'Clean Test Data',
      description: 'Delete all matches, events, and player positions. Teams and players are preserved.',
      icon: '🧹',
      color: 'bg-red-500 hover:bg-red-600',
      endpoint: '/api/testing/clean',
      method: 'DELETE'
    },
    {
      id: 'regenerate',
      title: 'Regenerate Players',
      description: 'Update player names to be unique per team with positions and jersey numbers.',
      icon: '🔄',
      color: 'bg-blue-500 hover:bg-blue-600',
      endpoint: '/api/testing/regenerate-players',
      method: 'POST'
    },
    {
      id: 'seed',
      title: 'Seed Clean Match',
      description: 'Create one pre-configured match with complete lineups (11 starters + 5 reserves per team).',
      icon: '⚽',
      color: 'bg-green-500 hover:bg-green-600',
      endpoint: '/api/testing/seed-match',
      method: 'POST'
    },
    {
      id: 'reset',
      title: 'Reset All',
      description: 'Execute all operations: Clean → Regenerate → Seed. Complete fresh start.',
      icon: '🔁',
      color: 'bg-purple-500 hover:bg-purple-600',
      endpoint: '/api/testing/reset-all',
      method: 'POST'
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🧪 Testing Tools
        </h1>
        <p className="text-gray-600">
          Admin utilities for managing test data and seeding clean matches.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tools.map(tool => (
          <div
            key={tool.id}
            className="bg-white rounded-lg shadow-md border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <span className="text-4xl mr-3">{tool.icon}</span>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {tool.description}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => executeAction(tool.id, tool.endpoint, tool.method)}
              disabled={loading[tool.id]}
              className={`w-full ${tool.color} text-white font-semibold py-3 px-4 rounded-lg 
                transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center`}
            >
              {loading[tool.id] ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Executing...
                </>
              ) : (
                `Execute ${tool.title}`
              )}
            </button>

            {results[tool.id] && (
              <div className={`mt-4 p-4 rounded-lg ${
                results[tool.id].success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm font-medium ${
                  results[tool.id].success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {results[tool.id].success ? '✅ ' : '❌ '}
                  {results[tool.id].message}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Component Test Pages */}
      <div className="mt-8 bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          🔬 Component Test Pages
        </h2>
        <p className="text-gray-600 mb-4">
          Isolated test pages for UI components and features.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/test/fonts"
            className="block p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all"
          >
            <div className="text-2xl mb-2">🔤</div>
            <div className="font-semibold">Font Test Page</div>
            <div className="text-sm opacity-90">7-segment LED fonts</div>
          </Link>
          <Link
            to="/test/buttons"
            className="block p-4 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-700 transition-all"
          >
            <div className="text-2xl mb-2">🔘</div>
            <div className="font-semibold">Button Test Lab</div>
            <div className="text-sm opacity-90">Ross-style backlit buttons</div>
          </Link>
          <Link
            to="/test/score"
            className="block p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
          >
            <div className="text-2xl mb-2">🎯</div>
            <div className="font-semibold">Score Display</div>
            <div className="text-sm opacity-90">Test scoreboard</div>
          </Link>
          <Link
            to="/test/clock"
            className="block p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all"
          >
            <div className="text-2xl mb-2">⏱️</div>
            <div className="font-semibold">Match Clock</div>
            <div className="text-sm opacity-90">Test timer controls</div>
          </Link>
          <Link
            to="/test/status"
            className="block p-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all"
          >
            <div className="text-2xl mb-2">📊</div>
            <div className="font-semibold">Match Status</div>
            <div className="text-sm opacity-90">Test status controls</div>
          </Link>
          <Link
            to="/test/png"
            className="block p-4 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-lg hover:from-pink-600 hover:to-pink-700 transition-all"
          >
            <div className="text-2xl mb-2">🖼️</div>
            <div className="font-semibold">PNG Transparency</div>
            <div className="text-sm opacity-90">Test image overlays</div>
          </Link>
          <Link
            to="/test/broadcast-min"
            className="block p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all"
          >
            <div className="text-2xl mb-2">📲</div>
            <div className="font-semibold">Broadcast-Min</div>
            <div className="text-sm opacity-90">iPad operator player styles</div>
          </Link>
        </div>
      </div>

      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-2xl">⚠️</span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Important Notes
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>All operations require admin privileges</li>
                <li>Clean operation preserves teams and players</li>
                <li>Reset All is recommended for a complete fresh start</li>
                <li>Operations cannot be undone</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestingTools;
